import React from "react";
import cx from "classnames";
import { Icon } from "@foris/avocado-icons";
import { Heading, Pill, Text } from "@foris/avocado-suite";
import { useTranslation } from "react-i18next";
import { EnrollmentRestriction } from "@models/ISchema";
import { getRestrictionValue } from "@utils/restrictions";
import styles from "./groupInheritedRestrictions.module.scss";

interface GroupInheritedRestrictionsProps {
  restrictions: {
    label: string;
    value: string;
    operatorLabel: string;
    self: EnrollmentRestriction;
  }[];
}

const GroupInheritedRestrictions = ({ restrictions = [] }: GroupInheritedRestrictionsProps) => {
  const { t } = useTranslation();

  return (
    <section className={styles.groupInheritedRestrictions}>
      <div className={styles.groupInheritedRestrictions_header}>
        <div className={styles.title}>
          <Icon name="lock" color="neutral-50" />
          <Heading type="h3">
            {t("group-restrictions.manage-restrictions.inherited-restrictions")}
          </Heading>
        </div>
        <Text className={styles.description}>
          {t("group-restrictions.manage-restrictions.inherited-restrictions-message")}
        </Text>
      </div>

      <div className={styles.groupInheritedRestrictions_content}>
        {restrictions?.map((restriction, index) => (
          <div key={restriction?.value} className={styles.contentRow}>
            <div className={styles.contentColumn}>
              {index === 0 && (
                <Text className={styles.contentColumn_title}>
                  {t("group-restrictions.manage-restrictions.form.property")}
                </Text>
              )}

              <ul className={styles.contentColumn_list}>
                <li className={styles.listItem}>
                  <Text type="sm" className={styles.listItem_text}>
                    {restriction?.label}
                  </Text>
                </li>
              </ul>
            </div>

            <div className={styles.contentColumn}>
              {index === 0 && (
                <Text className={styles.contentColumn_title}>
                  {t("group-restrictions.manage-restrictions.form.condition")}
                </Text>
              )}

              <ul className={styles.contentColumn_list}>
                <li className={styles.listItem}>
                  <Text
                    type="sm"
                    className={cx(styles.listItem_text, styles.listItem_text__italic)}
                  >
                    {restriction?.operatorLabel}
                  </Text>
                </li>
              </ul>
            </div>

            <div className={styles.contentColumn}>
              {index === 0 && (
                <Text className={styles.contentColumn_title}>
                  {t("group-restrictions.manage-restrictions.form.value")}
                </Text>
              )}

              <ul className={cx(styles.contentColumn_list, styles.contentColumn_list__tags)}>
                {restriction?.self?.values?.map(value => {
                  const valueData = getRestrictionValue(value);

                  return (
                    <li key={value?.id} className={styles.listItem}>
                      <Pill>
                        {valueData?.code} | {valueData?.name}
                      </Pill>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default GroupInheritedRestrictions;
